import { LoadingButton } from "@mui/lab";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { green, grey } from "@mui/material/colors";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import FetchHook from "../../../../Api/FetchHooks";
import PostHookAccount from "../../../../Api/PostHookAccount";
import PostHooks from "../../../../Api/PostHooks";
import AlertComponent from "../../../../Components/AlertComponent";
import Page from "../../../../Components/Page";
import TableComponent from "../../../../Components/TableComponent";
import UseServerTable from "../../../../Hooks/UseServerTable"; // Imported Hook
import {
  formatCurrencyV2,
  formatDate,
  getKycChip,
  getUserDetails,
  stringAvatar,
} from "../../../../utils/helpers";
import moment from "moment";
import {
  ArrowBack,
  Close,
  VerifiedUser,
  GppBad,
  AllInbox,
} from "@mui/icons-material"; // Added AllInbox for ErrorDisplay
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ErrorDisplay from "../../../../Components/ErrorDisplay";

const DetailItem = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4} lg={3}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight="medium">
      {value || "N/A"}
    </Typography>
  </Grid>
);

const UserAccountProfileV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selector = useSelector((state) => state);
  const LoggedInUser = getUserDetails();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const userAccounts = selector.usersData.userAccounts || [];

  // State
  const [bankAccNumber, setBankAccNumber] = useState(
    userAccounts.filter((acc) => acc.type === "bank"),
  );
  const [show, setShow] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState({});
  const [sms_supplier_acc_options, set_sms_supplier_acc_options] = useState([]);
  const [airtime_supplier_acc_options, set_airtime_supplier_acc_options] =
    useState([]);
  const [airtime_supplier_acc, set_airtime_supplier_acc] = useState();

  // Accounts State - Initialized from Redux if available
  const [shareholder_acc, set_shareholder_acc] = useState(
    userAccounts.filter((acc) => acc.type === "shareholder"),
  );
  const [sms_supplier_acc, set_sms_supplier_acc] = useState(
    userAccounts.filter((acc) => acc.type === "sms_supplier"),
  );
  const [sms_inventory_acc, set_sms_inventory_acc] = useState(
    userAccounts.filter((acc) => acc.type === "sms_inventory"),
  );

  console.log("sms_inventory_acc", sms_inventory_acc);
  console.log("sms_supplier_acc", sms_supplier_acc);

  const account = location.state?.account || {};

  // Hooks
  const [loading, , injectionRequest] = PostHookAccount();
  const [airtimeInjectionLoading, , airtimeInjectionReq] = PostHooks();
  const [accLoading, accResponse, accRequest] = FetchHook();
  const [smsAccLoading, smsAccResponse, smsAccRequest] = FetchHook();
  const [airtimeAccLoading, airtimeAccResponse, airtimeAccRequest] =
    FetchHook();

  // UseServerTable for Transactions
  const [transactions, setTransactions] = useState([]);
  const { gridProps, error: tableError } = UseServerTable({
    baseUrl: `/transaction?filter_value=${account.account_number}`,
    onSuccess: (data) => {
      setTransactions(data.Data);
    },
    // Only fetch if we have an account number
    enabled: !!account.account_number,
  });

  // Logic Handlers
  const handleModal = () => setShow(!show);
  const handleSnackbar = () => setOpenSnackbar(!openSnackbar);
  const showFeedback = (sev, msg) => {
    setSeverity({ severity: sev, message: msg });
    setOpenSnackbar(true);
  };

  // Initial Fetching - Optimized
  useEffect(() => {
    const fetchDependencies = () => {
      // If we don't have accounts in Redux, fetch them.
      // Or if the specific types we need are missing, we might want to fetch.
      // But typically UserAccounts page loads all accounts for the user.
      if (
        location.state?.user?.id &&
        (!userAccounts || userAccounts.length === 0) &&
        !accResponse
      ) {
        accRequest({ url: `account?entity_id='${location.state.user.id}'` });
      }

      if (account.type === "sms_inventory")
        smsAccRequest({ url: `account?type="sms_supplier"` });
      if (account.type === "airtime_inventory")
        airtimeAccRequest({ url: `account?type="airtime_supplier"` });
    };

    fetchDependencies();
  }, [account.type, location.state?.user?.id, userAccounts.length]); // Added userAccounts.length dependency

  // Effect for Table Error
  useEffect(() => {
    if (tableError) {
      showFeedback(tableError.severity, tableError.message);
    }
  }, [tableError]);

  // Effect for Accounts Data (from API call if needed)
  useEffect(() => {
    if (accResponse?.status === 200) {
      setBankAccNumber(
        accResponse.data.Loan.filter((el) => el.type === "bank"),
      );
      set_shareholder_acc(
        accResponse.data.Loan.filter((el) => el.type === "shareholder"),
      );
      set_sms_supplier_acc(
        accResponse.data.Loan.filter((el) => el.type === "sms_supplier"),
      );
      set_sms_inventory_acc(
        accResponse.data.Loan.filter((el) => el.type === "sms_inventory"),
      );
    }
    // If not fetching new data, ensure local state matches Redux (handling case where Redux updates externally)
    else if (userAccounts.length > 0 && !accResponse) {
      console.log("repeat");

      setBankAccNumber(userAccounts.filter((el) => el.type === "bank"));
      set_shareholder_acc(
        userAccounts.filter((el) => el.type === "shareholder"),
      );
      set_sms_supplier_acc(
        userAccounts.filter((el) => el.type === "sms_supplier"),
      );
      set_sms_inventory_acc(
        userAccounts.filter((el) => el.type === "sms_inventory"),
      );
    }

    if (smsAccResponse?.status === 200) {
      set_sms_supplier_acc_options(
        smsAccResponse.data.Loan.filter((el) => el.type === "sms_supplier"),
      );
    }
    if (airtimeAccResponse?.status === 200) {
      set_airtime_supplier_acc_options(airtimeAccResponse.data.Loan);
    }
  }, [accResponse, smsAccResponse, airtimeAccResponse, userAccounts]);

  const injectCapital = async (e) => {
    let formData = {};
    let response = null;

    try {
      if (account.type === "bank") {
        if (!shareholder_acc[0]?.account_number) {
          showFeedback("error", "Create a shareholders account!");
          return;
        }
        formData = {
          shareholder_account_no: shareholder_acc[0].account_number,
          mpesa_bank_accounta_no: bankAccNumber[0].account_number,
          amount: e.amount,
          loan_id: shareholder_acc[0].account_number,
          global_id: shareholder_acc[0].account_number,
        };
        response = await injectionRequest({ url: "api_5", body: formData });
      } else if (account.type === "shareholder") {
        if (!bankAccNumber[0]?.account_number) {
          showFeedback("error", "Create a bank account!");
          return;
        }
        formData = {
          shareholder_account_no: shareholder_acc[0].account_number,
          mpesa_bank_accounta_no: bankAccNumber[0].account_number,
          amount: e.amount,
          loan_id: bankAccNumber[0].account_number,
          global_id: bankAccNumber[0].account_number,
        };
        response = await injectionRequest({ url: "api_5", body: formData });
      } else if (
        account.type === "sms_supplier" ||
        account.type === "sms_inventory"
      ) {
        if (!sms_inventory_acc[0]?.account_number) {
          showFeedback("error", "Create an sms supplier or inventory account!");
          return;
        }
        formData = {
          supplier_account_no: sms_supplier_acc[0].account_number,
          inventory_account_no: sms_inventory_acc[0].account_number,
          amount: e.amount,
          loan_id: sms_supplier_acc[0].account_number,
          global_id: sms_inventory_acc[0].account_number,
        };
        response = await injectionRequest({ url: "api_7", body: formData });
      } else if (account.type === "airtime_inventory") {
        formData = {
          product_onwer_id: LoggedInUser.id,
          supplier_id: airtime_supplier_acc.entity_id,
          amount: e.amount,
        };
        response = await airtimeInjectionReq({
          url: "/purchase_inventory_airtime",
          body: formData,
        }); // Using hook standard for now
      } else {
        showFeedback("error", "Capital injection not allowed!");
        return;
      }

      // Handle Response
      if (
        response &&
        response.status === 200 &&
        response.data.StatusCode === 0
      ) {
        handleModal();
        showFeedback("success", "Capital Injection successful!");
        // Refresh table handled by UseServerTable?
        // Often UseServerTable exposes a refresh/fetch function.
        // gridProps.api.refreshServerSideStore() if available, but for now standard reload might be needed or just wait for next fetch.
        // Since UseServerTable doesn't expose a clear 'refetch' in this snippet (it does internal logic), we might skip manual refresh or force it by key.
        window.location.reload(); // Simplest "refresh" for now without deeper hook changes
      } else {
        const errorMsg =
          response?.data?.StatusMessage ||
          response?.response?.data?.message ||
          "Error occurred";
        showFeedback("error", errorMsg);
      }
    } catch (error) {
      showFeedback("error", error.message || "An unexpected error occurred");
    }
  };

  const columns = [
    {
      headerName: "Date",
      field: "transaction_timestamp",
      width: 200,
      valueGetter: (params) =>
        params.row.transaction_timestamp ||
        params.row.transaction_settlement_time,
      valueFormatter: (params) => formatDate(params.value),
    },
    { headerName: "Transaction ID", field: "transaction_id", width: 150 },
    {
      headerName: "Debit",
      field: "debit",
      valueFormatter: (params) => formatCurrencyV2(params.value),
    },
    {
      headerName: "Credit",
      field: "credit",
      valueFormatter: (params) => formatCurrencyV2(params.value),
    },
    {
      headerName: "Balance Before",
      field: "balance_before",
      width: 150,
      valueFormatter: (params) => formatCurrencyV2(params.value),
    },
    {
      headerName: "Balance After",
      field: "balance_after",
      width: 150,
      valueFormatter: (params) => formatCurrencyV2(params.value),
    },
    {
      headerName: "Status",
      field: "transaction_status",
      width: 150,
      renderCell: (params) => getKycChip(params?.value),
    },
    {
      headerName: "Description",
      field: "description",
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      headerName: "Settlement Time",
      field: "transaction_settlement_time",
      width: 200,
      valueFormatter: (params) => formatDate(params.value),
    },
    { headerName: "Source ID", field: "source_transaction_id", width: 150 },
    { headerName: "Account No", field: "account_number", width: 150 },
    { headerName: "Loan ID", field: "loan_id", width: 150 },
    { headerName: "Pair ID", field: "dr_cr_pair_id", width: 150 },
    { headerName: "Global ID", field: "global_request_id", width: 150 },
  ];

  return (
    <Page
      title="Account Profile"
      actions={
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      }
    >
      <AlertComponent
        open={openSnackbar}
        handleSnackbar={handleSnackbar}
        severity={severity}
      />

      {/* Header Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                {...stringAvatar(`${account.account_name} `)}
                sx={{
                  width: 64,
                  height: 64,
                  fontSize: 24,
                  bgcolor: "primary.main",
                }}
              />
              <Box>
                <Typography variant="h6">{account.account_name}</Typography>
                <Chip
                  label={account.type}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              {LoggedInUser.role === "admin" &&
                [
                  "bank",
                  "shareholder",
                  "sms_supplier",
                  "sms_inventory",
                  "airtime_inventory",
                ].includes(account.type) && (
                  <Button
                    variant="contained"
                    startIcon={<AccountBalanceIcon />}
                    onClick={handleModal}
                  >
                    Add Capital
                  </Button>
                )}
            </Stack>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <DetailItem label="Account Number" value={account.account_number} />
            <DetailItem
              label="Account Balance"
              value={
                <Typography
                  component="span"
                  sx={{ color: green[700], fontWeight: "bold" }}
                >
                  {formatCurrencyV2(account.account_balance)}
                </Typography>
              }
            />
            <DetailItem
              label="Description"
              value={account.account_description}
            />
            <DetailItem
              label="Start Date"
              value={moment(account.start_date).format("Do-MMM-YY, HH:mm:ss a")}
            />
          </Grid>
        </CardContent>
      </Card>

      {/* Transactions Card with UseServerTable */}
      <Card>
        <CardHeader title="Transactions" sx={{ pb: 0 }} />
        <Divider sx={{ mt: 1 }} />
        <CardContent>
          {transactions && transactions.length > 0 ? (
            <TableComponent
              columns={columns}
              data={transactions}
              getRowHeight={() => "auto"}
              {...gridProps}
            />
          ) : gridProps.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ErrorDisplay
              message="No transactions found"
              icon={
                <AllInbox color="secondary" sx={{ mx: "auto", fontSize: 80 }} />
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Capital Injection Dialog */}
      <Dialog
        open={show}
        onClose={handleModal}
        fullWidth
        maxWidth="xs"
        sx={{ borderRadius: "30%" }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Capital Injection</Typography>
            <Box onClick={handleModal} sx={{ cursor: "pointer" }}>
              <Close />
            </Box>
          </Stack>
          <Divider sx={{ mt: 1 }} />
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <form onSubmit={handleSubmit(injectCapital)}>
            <Stack spacing={2}>
              {account.type === "sms_inventory" && (
                <FormControl fullWidth>
                  <InputLabel>Supplier</InputLabel>
                  <Select
                    value={sms_supplier_acc}
                    label="Supplier"
                    onChange={(e) => set_sms_supplier_acc(e.target.value)}
                  >
                    {sms_supplier_acc_options.map((el, i) => (
                      <MenuItem value={el} key={i}>
                        {el.account_name} - {el.entity_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {account.type === "airtime_inventory" && (
                <FormControl fullWidth>
                  <InputLabel>Supplier</InputLabel>
                  <Select
                    value={airtime_supplier_acc}
                    label="Supplier"
                    onChange={(e) => set_airtime_supplier_acc(e.target.value)}
                  >
                    {airtime_supplier_acc_options.map((el, i) => (
                      <MenuItem value={el} key={i}>
                        {el.account_name} - {el.entity_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl fullWidth error={!!errors.amount}>
                <InputLabel htmlFor="amount">Amount</InputLabel>
                <OutlinedInput
                  id="amount"
                  type="number"
                  {...register("amount", { required: true })}
                  label="Amount"
                  onWheel={(event) => event.target.blur()}
                  inputProps={{ step: "any", min: 0 }}
                />
                {errors.amount && (
                  <FormHelperText>Amount is required</FormHelperText>
                )}
              </FormControl>

              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={2}
                sx={{ mt: 2 }}
              >
                <Button variant="outlined" onClick={handleModal}>
                  Cancel
                </Button>
                <LoadingButton
                  variant="contained"
                  type="submit"
                  loading={loading || airtimeInjectionLoading}
                >
                  Submit
                </LoadingButton>
              </Stack>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
    </Page>
  );
};

export default UserAccountProfileV2;
